import { useApi } from '@backstage/core-plugin-api';
import type {
  CustomFieldValidator,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import { useEffect, useState } from 'react';
import { projectsApiRef, type Project } from '../../api/projectsApi';

export interface ProjectValue {
  projectCode: string;
  environments: ('dev' | 'prod')[];
  confirmation: string;
}

const LISTBOX_PROPS = { style: { maxHeight: 280 } };

const filterOptions = createFilterOptions<Project>({
  stringify: project =>
    [project.projectCode, project.createdBy, project.templateName].join(' '),
});

const STATUS_LABELS = {
  deployed: 'deployed',
  failed: 'last deployment failed',
};

const describeProject = (project: Project) =>
  [
    project.environments
      .map(
        environment =>
          `${environment.name}: ${STATUS_LABELS[environment.status]}`,
      )
      .join(' · '),
    project.createdAt
      ? `created ${new Date(project.createdAt).toLocaleDateString()}`
      : undefined,
    project.createdBy ? `by ${project.createdBy}` : undefined,
  ]
    .filter(Boolean)
    .join(' — ');

const isConfirmed = (value?: ProjectValue) =>
  value?.confirmation?.trim() === value?.projectCode;

export const validateProject: CustomFieldValidator<ProjectValue | undefined> = (
  value,
  field,
) => {
  if (value?.projectCode && !isConfirmed(value))
    field.addError('Type the project code to confirm the destruction');
};

export const ProjectPicker = ({
  formData,
  formContext,
  onChange,
  rawErrors,
  required,
  disabled,
  readonly,
}: FieldExtensionComponentProps<ProjectValue>) => {
  const api = useApi(projectsApiRef);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const clientCode = (
    formContext?.formData as { clientName?: { code?: string } } | undefined
  )?.clientName?.code;

  useEffect(() => {
    const request = { active: true };
    setLoading(true);

    api
      .listProjects(clientCode)
      .then(result => {
        if (request.active) setProjects(result);
      })
      .catch(err => {
        if (request.active) setError(err);
      })
      .finally(() => {
        if (request.active) setLoading(false);
      });

    return () => {
      request.active = false;
    };
  }, [api, clientCode]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading projects...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Unable to load the projects.
      </Typography>
    );
  }

  if (projects.length === 0) {
    return (
      <Alert severity="info">
        No scaffolded project with infrastructure
        {clientCode ? ' found for this client' : ' found'}.
      </Alert>
    );
  }

  const selected =
    projects.find(project => project.projectCode === formData?.projectCode) ??
    null;
  const hasError = Boolean(rawErrors?.length);
  const isDisabled = disabled || readonly;
  const confirmationMismatch =
    Boolean(formData?.confirmation) && !isConfirmed(formData);

  const selectProject = (project: Project | null) =>
    onChange(
      project
        ? {
            projectCode: project.projectCode,
            environments: project.environments.map(
              environment => environment.name,
            ),
            confirmation: '',
          }
        : undefined,
    );

  const update = (change: Partial<ProjectValue>) =>
    formData && onChange({ ...formData, ...change });

  const toggleEnvironment = (name: 'dev' | 'prod', checked: boolean) => {
    const others = (formData?.environments ?? []).filter(
      environment => environment !== name,
    );
    update({ environments: checked ? [...others, name] : others });
  };

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Autocomplete<Project>
        options={projects}
        filterOptions={filterOptions}
        getOptionLabel={project => project.projectCode}
        getOptionSelected={(option, value) =>
          option.projectCode === value.projectCode
        }
        renderOption={project => (
          <Box display="flex" flexDirection="column">
            <Typography variant="body1">{project.projectCode}</Typography>
            <Typography variant="caption" color="textSecondary">
              {describeProject(project)}
            </Typography>
          </Box>
        )}
        value={selected}
        onChange={(_event, project) => selectProject(project)}
        disabled={isDisabled}
        ListboxProps={LISTBOX_PROPS}
        renderInput={params => (
          <TextField
            {...params}
            label="Project"
            required={required}
            error={hasError}
            helperText={
              hasError
                ? rawErrors?.join(', ')
                : 'Scaffolded projects with deployed infrastructure'
            }
          />
        )}
      />

      {selected && formData && (
        <>
          <FormControl component="fieldset" disabled={isDisabled}>
            <FormLabel component="legend">Environments to destroy</FormLabel>
            <FormGroup>
              {selected.environments.map(environment => (
                <FormControlLabel
                  key={environment.name}
                  control={
                    <Checkbox
                      checked={formData.environments.includes(environment.name)}
                      onChange={(_event, checked) =>
                        toggleEnvironment(environment.name, checked)
                      }
                    />
                  }
                  label={`${environment.name} (${
                    STATUS_LABELS[environment.status]
                  })`}
                />
              ))}
            </FormGroup>
          </FormControl>

          <Alert severity="warning">
            All the Azure resources of the selected environments will be
            permanently destroyed with <code>terraform destroy</code>. Their
            registered Argo CD manifests and kustomization references will be
            removed from GitOps first. The project repository is deleted only
            once all environments are destroyed. The request must first be
            approved before anything is deleted.
          </Alert>

          <TextField
            label={`Type "${selected.projectCode}" to confirm`}
            value={formData.confirmation ?? ''}
            onChange={event => update({ confirmation: event.target.value })}
            disabled={isDisabled}
            error={confirmationMismatch}
            helperText={
              confirmationMismatch
                ? 'The project code does not match'
                : undefined
            }
          />
        </>
      )}
    </Box>
  );
};
